import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from '@app/core';
import { NewsletterService } from '@app/domains/newsletter/services';
import { SharedModule } from '@app/shared';
import {
  TuiButton,
  TuiError,
  TuiHint,
  TuiIcon,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiButtonLoading,
  TuiCheckbox,
  TuiFieldErrorPipe,
  TuiFiles,
  TuiStepper,
  TuiTooltip,
  type TuiFileLike,
} from '@taiga-ui/kit';
import { CertificateService } from '../../services';

@Component({
  selector: 'app-ios-certificate-generator-form-section',
  imports: [
    SharedModule,
    TuiButton,
    TuiButtonLoading,
    TuiCheckbox,
    TuiError,
    TuiFieldErrorPipe,
    TuiFiles,
    TuiHint,
    TuiIcon,
    TuiLabel,
    TuiStepper,
    TuiTextfield,
    TuiTooltip,
  ],
  providers: [CertificateService],
  templateUrl: './ios-certificate-generator-form-section.component.html',
  styleUrl: './ios-certificate-generator-form-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IosCertificateGeneratorFormSectionComponent {
  protected readonly csrFormGroup = new FormGroup({
    fullName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    organization: new FormControl(''),
    organizationalUnit: new FormControl(''),
    city: new FormControl(''),
    state: new FormControl(''),
    country: new FormControl('', [Validators.maxLength(2)]),
    newsletter: new FormControl(true),
    privacy: new FormControl(false, [Validators.requiredTrue]),
  });
  protected readonly p12FormGroup = new FormGroup({
    certificateFile: new FormControl<TuiFileLike | null>(null, [
      Validators.required,
    ]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
    ]),
  });
  protected readonly activeStepIndex = signal(0);
  protected readonly step1State = signal<'error' | 'normal' | 'pass'>('normal');
  protected readonly step2State = signal<'error' | 'normal' | 'pass'>('normal');
  protected readonly loading = signal(false);
  protected readonly rejectedFile = signal<TuiFileLike | null>(null);
  protected readonly privateKey = signal<
    import('node-forge').pki.rsa.PrivateKey | null
  >(null);
  protected readonly subscribeNewsletter = this.newsletterService.subscribe();

  constructor(
    private readonly certificateService: CertificateService,
    private readonly newsletterService: NewsletterService,
    private readonly notificationsService: NotificationsService,
  ) {
    this.csrFormGroup.controls.newsletter.valueChanges.subscribe(value => {
      const privacyControl = this.csrFormGroup.controls.privacy;
      if (value) {
        privacyControl.setValidators([Validators.requiredTrue]);
        privacyControl.enable();
      } else {
        privacyControl.clearValidators();
        privacyControl.setValue(false);
        privacyControl.disable();
      }
      privacyControl.updateValueAndValidity();
    });
    this.p12FormGroup.controls.certificateFile.valueChanges.subscribe(
      async file => {
        if (!file) {
          return;
        }
        try {
          const arrayBuffer = await (file as File).arrayBuffer();
          await this.certificateService.validateCertificateFile(arrayBuffer);
        } catch {
          this.rejectedFile.set({
            name: file.name,
            content:
              'Invalid certificate file. Please upload a DER-encoded .cer file.',
          });
          this.p12FormGroup.controls.certificateFile.reset();
        }
      },
    );
  }

  protected onCertificateFileRemoveClick(): void {
    this.rejectedFile.set(null);
    this.p12FormGroup.controls.certificateFile.reset();
  }

  protected async onGenerateCsrSubmit(): Promise<void> {
    this.csrFormGroup.markAllAsTouched();
    if (this.csrFormGroup.invalid) {
      return;
    }
    const value = this.csrFormGroup.value;
    if (!value.fullName || !value.email) {
      return;
    }
    this.loading.set(true);
    try {
      if (value.newsletter) {
        try {
          await this.subscribeNewsletter.mutateAsync({ email: value.email });
        } catch {
          // Newsletter subscription is best-effort
        }
      }
      const result = await this.certificateService.generateIosCsr({
        commonName: value.fullName,
        email: value.email,
        organization: value.organization || undefined,
        organizationalUnit: value.organizationalUnit || undefined,
        locality: value.city || undefined,
        state: value.state || undefined,
        country: value.country || undefined,
      });
      this.privateKey.set(result.privateKey);
      this.certificateService.downloadFile(
        result.csrPem,
        'CertificateSigningRequest.certSigningRequest',
        'text/plain',
      );
      void this.notificationsService.showSuccessNotification(
        'The CSR was generated successfully.',
      );
      this.step1State.set('pass');
      this.activeStepIndex.set(1);
    } catch (error) {
      this.step1State.set('error');
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  protected async onGenerateP12Submit(): Promise<void> {
    this.p12FormGroup.markAllAsTouched();
    if (this.p12FormGroup.invalid) {
      return;
    }
    const key = this.privateKey();
    const file = this.p12FormGroup.controls.certificateFile
      .value as File | null;
    const password = this.p12FormGroup.value.password;
    if (!key || !file || !password) {
      return;
    }
    this.loading.set(true);
    try {
      const cerData = await file.arrayBuffer();
      const p12 = await this.certificateService.generateIosP12({
        privateKey: key,
        cerData,
        password,
      });
      this.certificateService.downloadFile(
        p12,
        'Certificates.p12',
        'application/x-pkcs12',
      );
      this.step2State.set('pass');
      void this.notificationsService.showSuccessNotification(
        'The certificate bundle was downloaded successfully.',
      );
    } catch (error) {
      this.step2State.set('error');
      throw error;
    } finally {
      this.loading.set(false);
    }
  }
}
